import { useState, useEffect } from 'react';
import {
  superAdminApi,
  type OrphanMachine,
  type OrphanVolume,
} from '../api/superAdmin';

export default function OrphansPage() {
  const [machines, setMachines] = useState<OrphanMachine[]>([]);
  const [volumes, setVolumes] = useState<OrphanVolume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchOrphans = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const response = await superAdminApi.getOrphans();
    setLoading(false);

    if (!response.success) {
      setError(response.error?.message || 'Failed to fetch orphan resources');
      return;
    }

    setMachines(response.data?.machines || []);
    setVolumes(response.data?.volumes || []);
  };

  useEffect(() => {
    fetchOrphans();
  }, []);

  const handleDeleteMachine = async (machine: OrphanMachine) => {
    if (
      !confirm(
        `Are you sure you want to delete orphan machine "${machine.name}" (${machine.id})?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(machine.id);
    setError(null);
    setSuccessMessage(null);

    const response = await superAdminApi.deleteOrphan('machine', machine.id);
    setDeletingId(null);

    if (!response.success) {
      setError(response.error?.message || 'Failed to delete machine');
      return;
    }

    setSuccessMessage(`Machine ${machine.name} deleted successfully`);
    fetchOrphans();
  };

  const handleDeleteVolume = async (volume: OrphanVolume) => {
    if (
      !confirm(
        `Are you sure you want to delete orphan volume "${volume.name}" (${volume.id})?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(volume.id);
    setError(null);
    setSuccessMessage(null);

    const response = await superAdminApi.deleteOrphan('volume', volume.id);
    setDeletingId(null);

    if (!response.success) {
      setError(response.error?.message || 'Failed to delete volume');
      return;
    }

    setSuccessMessage(`Volume ${volume.name} deleted successfully`);
    fetchOrphans();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const totalOrphans = machines.length + volumes.length;
  const hasOrphans = totalOrphans > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Orphan Resources</h2>
        <button
          onClick={fetchOrphans}
          disabled={loading}
          className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition disabled:opacity-50"
        >
          {loading ? 'Scanning...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {loading ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          Scanning for orphan resources...
        </div>
      ) : !hasOrphans ? (
        <div className="bg-green-500/10 border border-green-500 rounded-lg p-8 text-center">
          <div className="text-green-400 text-lg font-semibold mb-2">
            No Orphan Resources Found
          </div>
          <p className="text-green-400/70">
            All Fly Machines and Volumes are properly associated with instances.
          </p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 px-4 py-3 rounded">
            <p className="font-medium">
              Found {totalOrphans} orphan resource{totalOrphans > 1 ? 's' : ''}
            </p>
            <p className="text-sm mt-1">
              {machines.length} machine{machines.length !== 1 ? 's' : ''},{' '}
              {volumes.length} volume{volumes.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Orphan Machines */}
          {machines.length > 0 && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  Orphan Machines ({machines.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Region
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        State
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {machines.map((machine) => (
                      <tr key={machine.id} className="hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm text-white font-mono">
                          {machine.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                          {machine.id}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {machine.region}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={
                              machine.state === 'started' || machine.state === 'running'
                                ? 'text-green-400'
                                : machine.state === 'stopped'
                                ? 'text-gray-400'
                                : 'text-yellow-400'
                            }
                          >
                            {machine.state}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {formatDate(machine.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleDeleteMachine(machine)}
                            disabled={deletingId === machine.id}
                            className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deletingId === machine.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Orphan Volumes */}
          {volumes.length > 0 && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">
                  Orphan Volumes ({volumes.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Region
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Size
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        State
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {volumes.map((volume) => (
                      <tr key={volume.id} className="hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm text-white font-mono">
                          {volume.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                          {volume.id}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {volume.region}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {volume.size_gb} GB
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={
                              volume.state === 'created'
                                ? 'text-green-400'
                                : volume.state === 'pending_destroy'
                                ? 'text-yellow-400'
                                : 'text-gray-400'
                            }
                          >
                            {volume.state || 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {formatDate(volume.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleDeleteVolume(volume)}
                            disabled={deletingId === volume.id}
                            className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deletingId === volume.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Help Section */}
      <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">
          About Orphan Resources
        </h3>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <strong className="text-white">Orphan Machines:</strong> Fly Machines
            that exist but are not associated with any instance in the database.
          </p>
          <p>
            <strong className="text-white">Orphan Volumes:</strong> Fly Volumes
            that are not attached to any registered instance.
          </p>
          <p className="text-gray-500 mt-4">
            Orphan resources typically occur when instance deletion fails midway
            or when database records are manually removed. Deleting orphans helps
            reduce costs and clean up unused resources.
          </p>
        </div>
      </div>
    </div>
  );
}
